import type { Engine } from '@engine';
import { DANCES, decodeTerrain, primShapeNumber, type DanceId, type ServerMessage } from './net/protocol';

/** The slice of the engine World needs — lets tests use a fake. */
export type EngineSink = Pick<
  Engine,
  | 'upsertAvatar'
  | 'updateAvatars'
  | 'removeAvatar'
  | 'clearAvatars'
  | 'setRegion'
  | 'setAvatarDance'
  | 'setTerrain'
  | 'updatePrims'
  | 'removePrims'
  | 'clearPrims'
>;

export interface AvatarInfo {
  id: string;
  name: string;
  slot: number;
  self: boolean;
  dance: DanceId | null;
}

/** The engine's dance number: 1..8 for dance1..dance8, 0 for none (or an id we don't know). */
export function danceNumber(dance: DanceId | null | undefined): number {
  return dance ? DANCES.indexOf(dance) + 1 : 0;
}

const STRIDE = 8; // keep in sync with AVATAR_STRIDE in engine/src/lib.rs
const PRIM_STRIDE = 21; // keep in sync with PRIM_STRIDE in engine/src/lib.rs

/** Engine slots for ids, reusing freed ones so the engine's slot arrays stay compact. */
class Slots {
  private readonly byId = new Map<string, number>();
  private readonly free: number[] = [];
  private next = 0;

  get size(): number {
    return this.byId.size;
  }

  get(id: string): number | undefined {
    return this.byId.get(id);
  }

  /** The id's slot, allocating one if it has none. */
  take(id: string): number {
    let slot = this.byId.get(id);
    if (slot === undefined) {
      slot = this.free.pop() ?? this.next++;
      this.byId.set(id, slot);
    }
    return slot;
  }

  /** Frees the id's slot and returns it, or undefined if the id had none. */
  release(id: string): number | undefined {
    const slot = this.byId.get(id);
    if (slot === undefined) return undefined;
    this.byId.delete(id);
    this.free.push(slot);
    return slot;
  }

  clear(): void {
    this.byId.clear();
    this.free.length = 0;
    this.next = 0;
  }
}

/**
 * Applies world-state messages to the engine. Owns the avatar id → engine slot map,
 * reusing freed slots so the engine's slot array stays compact.
 */
export class World {
  private readonly avatars = new Map<string, AvatarInfo>();
  private readonly freeSlots: number[] = [];
  private nextSlot = 0;
  private packed = new Float32Array(STRIDE * 64);
  private readonly prims = new Slots();
  private primPacked = new Float32Array(PRIM_STRIDE * 256);

  regionName = '';
  selfId: string | null = null;

  constructor(private readonly engine: EngineSink) {}

  get avatarCount(): number {
    return this.avatars.size;
  }

  get primCount(): number {
    return this.prims.size;
  }

  avatar(id: string): AvatarInfo | undefined {
    return this.avatars.get(id);
  }

  /** What our own avatar is dancing, as the grid reports it. */
  get selfDance(): DanceId | null {
    if (this.selfId === null) return null;
    return this.avatars.get(this.selfId)?.dance ?? null;
  }

  /** Returns true if the message was a world-state message. */
  apply(message: ServerMessage): boolean {
    switch (message.type) {
      case 'region':
        this.regionName = message.name;
        // setRegion drops the engine's prims too; the new region's follow this message.
        this.engine.setRegion(message.sizeX, message.sizeY, message.waterHeight);
        this.prims.clear();
        return true;

      case 'objectsUpdated': {
        // One boundary crossing per network batch, as for movement.
        if (this.primPacked.length < message.objects.length * PRIM_STRIDE) {
          this.primPacked = new Float32Array(message.objects.length * PRIM_STRIDE * 2);
        }
        let n = 0;
        for (const p of message.objects) {
          // parseServerMessage checks the envelope only; a short array would leave the previous
          // prim's floats in place, a long one would spill into the next field.
          if (
            p.position.length !== 3 ||
            p.rotation.length !== 4 ||
            p.scale.length !== 3 ||
            p.color.length !== 4
          ) {
            continue;
          }
          const o = n++ * PRIM_STRIDE;
          this.primPacked[o] = this.prims.take(p.id);
          this.primPacked[o + 1] = primShapeNumber(p.shape);
          this.primPacked.set(p.position, o + 2);
          this.primPacked.set(p.rotation, o + 5);
          this.primPacked.set(p.scale, o + 9);
          this.primPacked.set(p.color, o + 12);
          // The optional look fields: absent means the default. The engine clamps the ranges
          // again (it never trusts the wire); here only the types are checked.
          this.primPacked[o + 16] = p.fullbright === true ? 1 : 0;
          this.primPacked[o + 17] = typeof p.glow === 'number' ? p.glow : 0;
          this.primPacked[o + 18] = typeof p.hollow === 'number' ? p.hollow : 0;
          const cut = Array.isArray(p.profileCut) && p.profileCut.length === 2 ? p.profileCut : [0, 1];
          this.primPacked[o + 19] = typeof cut[0] === 'number' ? cut[0] : 0;
          this.primPacked[o + 20] = typeof cut[1] === 'number' ? cut[1] : 1;
        }
        if (n > 0) this.engine.updatePrims(this.primPacked.subarray(0, n * PRIM_STRIDE));
        return true;
      }

      case 'objectsRemoved': {
        const slots: number[] = [];
        for (const id of message.ids) {
          const slot = this.prims.release(id);
          if (slot !== undefined) slots.push(slot);
        }
        if (slots.length > 0) this.engine.removePrims(Uint32Array.from(slots));
        return true;
      }

      case 'terrain': {
        const heights = decodeTerrain(message);
        if (heights) this.engine.setTerrain(message.samplesX, message.samplesY, message.spacing, heights);
        else console.warn('Ignoring terrain: sizes, range or data out of bounds');
        return true;
      }

      case 'avatarAdded': {
        const existing = this.avatars.get(message.id);
        const slot = existing?.slot ?? this.allocateSlot();
        const dance = message.dance ?? null;
        this.avatars.set(message.id, {
          id: message.id,
          name: message.name,
          slot,
          self: message.self,
          dance,
        });
        if (message.self) this.selfId = message.id;
        const [x, y, z] = message.position;
        const [qx, qy, qz, qw] = message.rotation;
        this.engine.upsertAvatar(slot, x, y, z, qx, qy, qz, qw, message.self);
        // upsertAvatar resets the engine's avatar, so set its dance after (even "none").
        this.engine.setAvatarDance(slot, danceNumber(dance));
        return true;
      }

      case 'avatarDance': {
        const info = this.avatars.get(message.id);
        if (!info) return true; // dance for an avatar we haven't seen: ignore
        info.dance = message.dance;
        this.engine.setAvatarDance(info.slot, danceNumber(message.dance));
        return true;
      }

      case 'avatarMoved': {
        // One boundary crossing per network batch, not per avatar.
        const needed = message.updates.length * STRIDE;
        if (this.packed.length < needed) this.packed = new Float32Array(needed * 2);
        let n = 0;
        for (const u of message.updates) {
          const info = this.avatars.get(u.id);
          if (!info) continue; // moved before added: ignore
          const o = n * STRIDE;
          this.packed[o] = info.slot;
          this.packed.set(u.position, o + 1);
          this.packed.set(u.rotation, o + 4);
          n++;
        }
        if (n > 0) this.engine.updateAvatars(this.packed.subarray(0, n * STRIDE));
        return true;
      }

      case 'avatarRemoved': {
        const info = this.avatars.get(message.id);
        if (!info) return true;
        this.avatars.delete(message.id);
        this.freeSlots.push(info.slot);
        this.engine.removeAvatar(info.slot);
        if (this.selfId === message.id) this.selfId = null;
        return true;
      }

      default:
        return false;
    }
  }

  reset(): void {
    this.avatars.clear();
    this.freeSlots.length = 0;
    this.nextSlot = 0;
    this.selfId = null;
    this.engine.clearAvatars();
    this.prims.clear();
    this.engine.clearPrims();
  }

  private allocateSlot(): number {
    return this.freeSlots.pop() ?? this.nextSlot++;
  }
}
